import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  extractProjectUpload,
  useCreateProjectUploadUrl,
  useExtractProjectUpload,
} from '@/entities/projectupload/api/generated/api';
import type { ProjectUploadExtractionResponse } from '@/entities/projectupload/api/generated/model';
import { isTokenLimitError } from '@/entities/system/lib/error-guards';
import { uploadToS3WithFields } from '@/shared/api/upload';
import { usePdfExtractionStore } from '@/shared/stores/pdf-extraction-store';
import { useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import { toast } from '@/shared/ui';
import { POLLING_CONFIG } from '../constants';
import type { CreateFromPdfActions, CreateFromPdfState, ViewStatus } from '../types';

// 解析完了の場合のみフォームが編集されていると判断する
const dirtyStatutes: ViewStatus[] = ['completed'];

/**
 * PDF解析のステートとアクションを管理するカスタムフック
 *
 * @returns 状態とアクション
 */
export const useCreateFromPdf = (): CreateFromPdfState & CreateFromPdfActions => {
  const { t } = useTranslation();
  const setHasUnsavedChanges = useRouteSidePanelStore((s) => s.setHasUnsavedChanges);

  // Zustandストアから解析結果を設定
  const setExtractionResultStore = usePdfExtractionStore((state) => state.setExtractionResult);

  const [viewStatus, setViewStatus] = useState<ViewStatus>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [extractionResult, _setExtractionResult] = useState<ProjectUploadExtractionResponse | null>(
    null,
  );

  const setExtractionResult = (result: ProjectUploadExtractionResponse | null) => {
    // フォーム側にも解析結果を設定するためにsetExtractionResultStoreにも渡す
    setExtractionResultStore(result);
    _setExtractionResult(result);
  };

  // Presigned URL取得用mutation
  const createUploadUrlMutation = useCreateProjectUploadUrl();

  // PDF解析用mutation
  const extractProjectUploadMutation = useExtractProjectUpload();

  /**
   * GuardDutyスキャン完了までポーリング
   *
   * @param objectKey - S3オブジェクトキー
   * @returns 解析結果
   */
  const pollUntilScanComplete = async (
    objectKey: string,
  ): Promise<ProjectUploadExtractionResponse> => {
    for (let attempt = 1; attempt <= POLLING_CONFIG.maxRetries; attempt++) {
      const response = await extractProjectUpload({ object_key: objectKey });

      // スキャン完了
      if (!response.is_scanning) {
        return response;
      }

      // 最大リトライ回数に達した場合
      if (attempt === POLLING_CONFIG.maxRetries) {
        throw new Error('GuardDuty scan timeout');
      }

      // 次のポーリングまで待機
      await new Promise((resolve) => setTimeout(resolve, POLLING_CONFIG.interval));
    }

    // 理論上到達しないが、TypeScriptの型安全性のため
    throw new Error('pollUntilScanComplete: unexpected loop completion');
  };

  /**
   * ファイル選択時のハンドラ
   */
  const handleFileDrop = (files: File[]) => {
    const file = files[0];
    if (file) {
      setSelectedFile(file);
      setViewStatus('fileSelected');
    }
  };

  /**
   * ファイル削除時のハンドラ
   */
  const handleFileRemove = () => {
    setSelectedFile(null);
    setViewStatus('idle');
    setExtractionResult(null);
  };

  /**
   * PDF解析を実行
   */
  const handleAnalyze = async () => {
    if (!selectedFile) return;

    try {
      setViewStatus('uploading');

      // 1. Presigned POST URLを取得
      const uploadUrlResponse = await createUploadUrlMutation.mutateAsync({
        data: {
          file_name: selectedFile.name,
        },
      });
      const { url: presignedUrl, object_key: objectKey, fields } = uploadUrlResponse;

      // 2. PDFをS3にアップロード（Presigned POST方式）
      await uploadToS3WithFields({
        presignedUrl,
        file: selectedFile,
        fields,
      });

      setViewStatus('analyzing');

      // 3. PDF解析を実行
      const extractionResponse = await extractProjectUploadMutation.mutateAsync({
        data: {
          object_key: objectKey,
        },
      });

      // 4. GuardDutyスキャン中の場合はポーリング
      if (extractionResponse.is_scanning) {
        setViewStatus('scanning');
        const finalResponse = await pollUntilScanComplete(objectKey);
        setExtractionResult(finalResponse);
      } else {
        setExtractionResult(extractionResponse);
      }

      setViewStatus('completed');
      toast.success(t('ai.pdf-upload.completed-message'));
    } catch (error) {
      if (isTokenLimitError(error)) {
        // 402（トークン制限超過）はインターセプターでトースト表示済み、専用UIを表示
        setViewStatus('tokenLimitExceeded');
      } else {
        setViewStatus('error');
        toast.error(t('ai.errors.analysis-failed-title'));
      }
    }
  };

  /**
   * 再実行
   */
  const handleRetry = () => {
    setViewStatus('idle');
    setSelectedFile(null);
    setExtractionResult(null);
  };

  // viewStatusに基づいて未保存変更の有無を更新
  // コンポーネントがアンマウントされたときは未保存変更をリセット
  useEffect(() => {
    if (dirtyStatutes.includes(viewStatus)) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }

    return () => {
      setHasUnsavedChanges(false);
      // ストアに残留した解析結果をクリアし、作成画面での意図しない自動反映を防ぐ
      setExtractionResultStore(null);
    };
  }, [viewStatus, setHasUnsavedChanges, setExtractionResultStore]);

  return {
    // State
    viewStatus,
    selectedFile,
    extractionResult,
    // Actions
    handleFileDrop,
    handleFileRemove,
    handleAnalyze,
    handleRetry,
  };
};
